import { useEffect, useCallback } from "react";
import { useAppDispatch } from "@/store/store";
import { processQueueThunk } from "./ticketQueueOperations";
import { initQueueThunk } from "./ticketQueueOperations";
import { env } from "@/services/env.service";

/**
 * Custom hook to manage queue initialization, processing, and automatic retries
 *
 * Features:
 * - Initializes queue from IndexedDB on mount
 * - Processes queue when online
 * - Auto-processes queue when connection is restored
 * - Retries pending items every 30 seconds (when online)
 */
export function useQueueManager() {
  const dispatch = useAppDispatch();

  const handleProcessQueue = useCallback(() => {
    dispatch(processQueueThunk());
  }, [dispatch]);

  useEffect(() => {
    if (!env.FF_OFFLINE_MODE) return;

    // Initialize Queue from IndexedDB
    dispatch(initQueueThunk());

    // Process queue if online on mount
    if (navigator.onLine) {
      handleProcessQueue();
    }

    // Process queue when coming online
    const handleOnline = () => {
      console.log("🌐 Connection restored - processing queue...");
      handleProcessQueue();
    };

    window.addEventListener("online", handleOnline);

    // Set up interval to retry pending items every 30 seconds (only when online)
    const retryInterval = setInterval(() => {
      if (navigator.onLine) {
        console.log("🔄 Retry interval - checking queue...");
        handleProcessQueue();
      }
    }, 30000); // 30 seconds

    return () => {
      window.removeEventListener("online", handleOnline);
      clearInterval(retryInterval);
    };
  }, [dispatch, handleProcessQueue]);

  return {
    processQueue: handleProcessQueue,
  };
}
